import {
    Form,
    FormButtonRow,
    FormGroupContext,
    ProcessItemContainer,
    ProcessItemHeader,
    Radio,
    RadioGroup,
} from '@ksyos/design-system';
import { useEffect, useMemo, useState } from 'react';
import type { IOsasnV1CustomControlData } from '@common/interfaces/somnology';
import { FilesUrlBase } from '@common/enums';
import { contactFileExtensions } from '@common/utils/fileExtensions';
import { Field } from '@frontend/components/elements/Field';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { useEpisodeContext } from '@frontend/utils';
import type { ICustomControlRequestAddEditProps } from '../../../interfaces/customControlRequestAddEditProps';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../../common/enums/toastType';
import { getSelectedLanguage } from '../../../../../../translate';
import { showServiceToast } from '../../../../../../utils/showToast';
import { CancelButton } from '../../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../../elements/carePathButtons/SaveSendButton';
import { HeaderDivider } from '../../../../../elements/common';
import { TextAreaField } from '../../../../../fields/TextArea';
import { AssetDropzoneWithActions } from '../../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../../utils/v1/CareProviderSelector';
import { getCareProviderIsActive } from '../../../utils/v1/getCareProviderIsActive';
import { getTitle } from './common';

export function OsasnV1RequestAddEdit(props: ICustomControlRequestAddEditProps<IOsasnV1CustomControlData>) {
    const [buttonDisabled, setButtonDisabled] = useState<boolean>();

    const { patient } = useEpisodeContext();
    const [isCareProviderActive, setIsCareProviderActive] = useState<boolean | undefined>();

    useEffect(() => {
        loadIsPatientCareProviderActive();
    }, []);

    const title = useMemo(
        () => getTitle(props.service.code, props.service.name, props.request.customControlData.title),
        [props.service.code, props.service.name, props.request.customControlData.title]
    );

    return (
        <ProcessItemContainer>
            <ProcessItemHeader
                color="secondary"
                icon="contact"
                organizationName={props.process?.requesterOrganization?.name}
                processId={props.process?.processId.toString()}
                title={title}
            />
            <FormGroupContext.Provider value={{ labelWidth: 'one-quarter', size: 'medium', variant: 'outline' }}>
                <Form submitOnEnter={false}>
                    <FormContextProvider
                        value={{
                            customControlData: true,
                            errors: props.errors,
                            item: props.request.customControlData,
                            onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                            onDoValidate: async (key) => await props.validateOneCustomControl(key),
                            placeholderGroupName: 'request',
                        }}
                    >
                        {isCareProviderActive === false && (
                            <CareProviderSelector
                                {...props}
                                entity={props.request}
                            />
                        )}
                        <TextAreaField
                            fieldKey="title"
                            label={getSelectedLanguage().osasNotitie.noteTitleLabel}
                            placeholder={getSelectedLanguage().osasNotitie.noteTitlePlaceholder}
                            variant="outline"
                        />
                        <TextAreaField
                            fieldKey="text"
                            label={getSelectedLanguage().osasNotitie.noteTextLabel}
                            placeholder=""
                            rows={2}
                            variant="outline"
                        />

                        <Field label={getSelectedLanguage().osasNotitie.filesLabel}>
                            <AssetDropzoneWithActions
                                acceptedFiles={contactFileExtensions}
                                assets={props.request.assets}
                                assetsBaseUrl={FilesUrlBase.requests}
                                onChange={async (assets) => await props.handleChange('assets', assets)}
                                onUploading={async (isUploading) => setButtonDisabled(isUploading)}
                                processActivityId={props.request.id}
                                service={props.service}
                                showAdditionalFilesCheckbox={props.previousRequest !== undefined}
                                updateNewRequestPreviousRequest={props.updateNewRequestPreviousRequest}
                            />
                        </Field>

                        <HeaderDivider text={getSelectedLanguage().osasNotitie.informHeader} />

                        <Field
                            fieldKey="informGeneralPractitioner"
                            label={getSelectedLanguage().osasNotitie.informGeneralPractitionerLabel}
                        >
                            <RadioGroup
                                layout="row"
                                name="informGeneralPractitioner"
                                onChange={async (event) =>
                                    await props.handleChangeCustomControl<IOsasnV1CustomControlData>({
                                        informGeneralPractitioner: event.target.value === 'true',
                                    })
                                }
                                value={Boolean(props.request.customControlData.informGeneralPractitioner).toString()}
                            >
                                <Radio
                                    label={getSelectedLanguage().common.yes}
                                    value="true"
                                />
                                <Radio
                                    label={getSelectedLanguage().common.no}
                                    value="false"
                                />
                            </RadioGroup>
                        </Field>
                    </FormContextProvider>

                    <FormButtonRow>
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.request}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveRequest('draft');
                            }}
                        />
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveRequest('final');
                            }}
                        />
                    </FormButtonRow>
                </Form>
            </FormGroupContext.Provider>
        </ProcessItemContainer>
    );

    async function loadIsPatientCareProviderActive() {
        const isPatientCareProviderActive = await getCareProviderIsActive(
            patient.careProviderPractitionerId,
            patient.careProviderOrganizationId
        );
        setIsCareProviderActive(isPatientCareProviderActive);

        if (props.request?.draftCareProviderPractitionerId) {
            if (isPatientCareProviderActive) {
                if (
                    props.request?.draftCareProviderPractitionerId !== patient.careProviderPractitionerId ||
                    props.request?.draftCareProviderOrganizationId !== patient.careProviderOrganizationId
                ) {
                    await props.handleChange('draftCareProviderOrganizationId', patient.careProviderOrganizationId!);
                    await props.handleChange('draftCareProviderPractitionerId', patient.careProviderPractitionerId!);
                }
            } else {
                const isPatientDraftCareProviderActive = await getCareProviderIsActive(
                    props.request?.draftCareProviderPractitionerId,
                    props.request?.draftCareProviderOrganizationId
                );
                if (!isPatientDraftCareProviderActive) {
                    await props.handleChange('draftCareProviderOrganizationId', null);
                    await props.handleChange('draftCareProviderPractitionerId', null);
                }
            }
        }
    }

    async function saveRequest(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedRequest = await props.validateRequest(saveType);
        if (!validatedRequest) {
            setButtonDisabled(false);

            return;
        }
        const requestId = await props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'request',
                getSelectedLanguage().toast.submitDefaultMessage
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'request',
                getSelectedLanguage().toast.draftDefaultMessage
            );
        }
        await props.onSave(requestId);
    }
}

export function osasnV1RequestInitializeCustomControlData(): IOsasnV1CustomControlData {
    return {
        informGeneralPractitioner: false,
        text: null,
        title: null,
    };
}
